/**
 * UsageMeter - API usage indicator component
 */

import { cn } from '@/utils/cn';
import { useSubscriptionStore, selectApiUsage } from '@/stores';

interface UsageMeterProps {
  compact?: boolean;
}

export function UsageMeter({ compact = false }: UsageMeterProps) {
  const { used, limit, percentage } = useSubscriptionStore(selectApiUsage);

  const getStatusColor = () => {
    if (percentage >= 90) return 'text-red-500';
    if (percentage >= 75) return 'text-yellow-500';
    return 'text-hud-primary';
  };

  const getBarColor = () => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-hud-primary';
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-16 h-1.5 bg-glass-border rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-300', getBarColor())}
            style={{ width: `${Math.min(100, percentage)}%` }}
          />
        </div>
        <span className={cn('text-xs font-mono', getStatusColor())}>
          {percentage}%
        </span>
      </div>
    );
  }

  return (
    <div className="p-4 rounded-xl bg-glass-bg border border-glass-border">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-text-secondary">API Usage</span>
        <span className={cn('text-sm font-mono', getStatusColor())}>
          {used.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', getBarColor())}
          style={{ width: `${Math.min(100, percentage)}%` }}
        />
      </div>

      {/* Status message */}
      <p className="mt-2 text-xs text-text-muted">
        {percentage >= 90 ? (
          <span className="text-red-400">
            Almost at limit! Consider upgrading for more API calls.
          </span>
        ) : percentage >= 75 ? (
          <span className="text-yellow-400">
            You've used {percentage}% of your monthly limit.
          </span>
        ) : (
          <span>
            {limit - used} API calls remaining this month.
          </span>
        )}
      </p>
    </div>
  );
}
